"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import FillArrowDown from "@crehana/compass.icons.fill/FillArrowDown";
import ArrowIosLeft from "@crehana/compass.icons.outline/ArrowIosLeft";
import ArrowIosRight from "@crehana/compass.icons.outline/ArrowIosRight";
import { format } from "date-fns";
import { DayPicker, useNavigation } from "react-day-picker";
import { cn } from "../utils.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../Select/index.js";
function createArrayFromRange(from = new Date().getFullYear() - 80, to = new Date().getFullYear() + 10) {
    const fixedTo = to < from ? from : to;
    return Array.from({
        length: fixedTo - from + 1
    }, (_, i)=>from + i);
}
function CustomCaption(props) {
    const { goToMonth, nextMonth, previousMonth } = useNavigation();
    const btnClassName = (enabled)=>cn('tw-grow-2', {
            'tw-opacity-50 tw-cursor-not-allowed': !enabled
        });
    const yearOptions = createArrayFromRange(props.fromYear, props.toYear);
    return /*#__PURE__*/ jsxs("div", {
        className: "tw-w-full tw-flex tw-gap-0.4 tw-items-center tw-justify-between tw-p-0.4",
        children: [
            /*#__PURE__*/ jsx("button", {
                type: "button",
                disabled: !previousMonth,
                onClick: ()=>previousMonth && goToMonth(previousMonth),
                className: btnClassName(Boolean(previousMonth)),
                children: /*#__PURE__*/ jsx(ArrowIosLeft, {
                    size: 24,
                    className: "tw-w-2.4 tw-h-2.4 tw-bg-transparent tw-p-0 tw-text-primary-light-500"
                })
            }),
            /*#__PURE__*/ jsx("div", {
                className: "tw-w-[22.4rem] tw-text-center tw-font-body4 tw-capitalize",
                children: props.yearDropdown ? /*#__PURE__*/ jsxs("span", {
                    className: "tw-flex tw-items-center tw-justify-center tw-gap-0.4",
                    children: [
                        format(props.displayMonth, 'MMMM', {
                            locale: props.locale
                        }),
                        /*#__PURE__*/ jsxs(Select, {
                            defaultValue: props.displayMonth.getFullYear().toString(),
                            value: props.displayMonth.getFullYear().toString(),
                            onValueChange: (value)=>{
                                const newDate = new Date(props.displayMonth);
                                newDate.setFullYear(parseInt(value));
                                goToMonth(newDate);
                            },
                            children: [
                                /*#__PURE__*/ jsx(SelectTrigger, {
                                    className: "tw-text-primary-light-500 tw-font-subtitle5 dark:tw-text-primary-dark-400",
                                    unstyled: true,
                                    children: /*#__PURE__*/ jsxs("span", {
                                        className: "tw-flex tw-items-center tw-gap-0.4",
                                        children: [
                                            /*#__PURE__*/ jsx(SelectValue, {}),
                                            /*#__PURE__*/ jsx(FillArrowDown, {
                                                className: "tw-text-inherit",
                                                size: 16
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ jsx(SelectContent, {
                                    children: yearOptions.map((year)=>/*#__PURE__*/ jsx(SelectItem, {
                                            value: year.toString(),
                                            children: year
                                        }, year))
                                })
                            ]
                        })
                    ]
                }) : format(props.displayMonth, 'MMMM yyy', {
                    locale: props.locale
                })
            }),
            /*#__PURE__*/ jsx("button", {
                type: "button",
                disabled: !nextMonth,
                onClick: ()=>nextMonth && goToMonth(nextMonth),
                className: btnClassName(Boolean(nextMonth)),
                children: /*#__PURE__*/ jsx(ArrowIosRight, {
                    size: 24,
                    className: "tw-w-2.4 tw-h-2.4 tw-bg-transparent tw-p-0 tw-text-primary-light-500"
                })
            })
        ]
    });
}
function Calendar({ className, classNames, showOutsideDays = true, yearDropdown, footer, ...props }) {
    const isSingleMode = 'single' === props.mode;
    const isRangeMode = 'range' === props.mode;
    return /*#__PURE__*/ jsx(DayPicker, {
        locale: props.locale,
        showOutsideDays: showOutsideDays,
        className: cn('tw-px-1.6 tw-pt-2 tw-pb-3.2 tw-shadow-dropdown tw-rounded dark:tw-text-neutral-light-100', className),
        classNames: {
            months: 'tw-flex tw-flex-col',
            month: '',
            caption: 'tw-flex tw-justify-center tw-pt-0.4 tw-relative tw-items-center',
            caption_label: 'tw-font-body4',
            nav: 'tw-space-x-0.4 flex items-center',
            nav_button: 'tw-w-2.4 tw-h-2.4 tw-bg-transparent tw-p-0 tw-text-primary-light-500',
            table: 'tw-mt-1.2 tw-block tw-w-full',
            head_row: 'tw-flex tw-justify-between tw-px-0.4 tw-pb-2',
            head_cell: 'tw-font-label1 tw-uppercase tw-block tw-w-2.4 tw-h-[1.8rem] tw-text-center tw-text-neutral-light-600',
            tbody: 'tw-space-y-1.2',
            row: 'tw-flex tw-flex-row',
            cell: 'tw-p-0 tw-cursor-pointer focus-within:tw-relative',
            day: cn('tw-w-[4.2rem] tw-h-2.4 tw-text-center tw-font-body4', isSingleMode && 'hover:tw-bg-primary-light-300 hover:tw-text-neutral-light-100 tw-rounded-[40px] dark:hover:tw-bg-primary-dark-400'),
            day_outside: 'tw-text-neutral-light-700 dark:tw-text-neutral-dark-600 aria-selected:tw-text-neutral-light-100 dark:aria-selected:tw-text-neutral-light-100',
            day_selected: cn(isSingleMode && 'tw-bg-primary-light-500 hover:tw-bg-primary-light-500 dark:hover:tw-bg-primary-light-500 tw-text-neutral-light-100 tw-rounded-[40px]', isRangeMode && 'tw-bg-primary-light-500 hover:tw-bg-primary-light-500 dark:hover:tw-bg-primary-light-500 tw-text-neutral-light-100'),
            day_range_start: 'tw-rounded-l-full tw-bg-primary-light-500 tw-text-neutral-light-100',
            day_range_end: 'tw-rounded-r-full tw-bg-primary-light-500 tw-text-neutral-light-100',
            day_today: 'tw-font-semibold',
            day_disabled: 'tw-opacity-20',
            day_range_middle: 'aria-selected:bg-slate-100 aria-selected:text-slate-900 dark:aria-selected:bg-slate-800 dark:aria-selected:text-slate-50',
            day_hidden: 'tw-invisible',
            ...classNames
        },
        components: {
            Caption: (captionProps)=>/*#__PURE__*/ jsx(CustomCaption, {
                    locale: props.locale,
                    yearDropdown: yearDropdown,
                    fromYear: props.fromYear ?? props.fromDate?.getFullYear(),
                    toYear: props.toYear ?? props.toDate?.getFullYear(),
                    ...captionProps
                })
        },
        footer: footer,
        ...props
    });
}
Calendar.displayName = 'Calendar';
export { Calendar };
